import React, { useState, useEffect } from 'react';
import CalculationResult from './component/CalculationResult';
import axios from 'axios';
import Header from './component/Header';
import Footer from './component/Footer';
import AnalyticsConsent from './component/AnalyticsConsent';
import { ThemeProvider } from './context/ThemeContext';
import { ChatbotProvider } from './context/ChatbotContext';
import ChatButton from './component/ChatButton';
import Chatbot from './component/Chatbot';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Seo from './component/Seo';
import { getPageTitle, getPageDescription } from './config/seo';
import { HelmetProvider } from 'react-helmet-async';
import GoogleTagManager from './component/GoogleTagManager';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider>
                <ChatbotProvider>
                    <GoogleTagManager />
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<CalculatorLayout />} />
                            <Route path="/house" element={<CalculatorLayout />} />
                            <Route path="/mortgage" element={<CalculatorLayout />} />
                        </Routes>
                    </BrowserRouter>
                </ChatbotProvider>
            </ThemeProvider>
        </HelmetProvider>
    );
}

function CalculatorLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Sync activeTab with URL
    const activeTab = location.pathname === '/' ? 'investment' : 
                     location.pathname === '/house' ? 'house' : 'mortgage';

    const setActiveTab = (tab) => {
        const path = tab === 'investment' ? '/' : `/${tab}`;
        navigate(path);
    };

    const [calculationResult, setCalculationResult] = useState(null);
    const [investments, setInvestments] = useState(() => {
        const saved = localStorage.getItem('investments');
        return saved ? JSON.parse(saved) : [
            { id: 1, principal: '', annualInterestRate: '' }
        ];
    });
    const [timesPerYear, setTimesPerYear] = useState(() => 
        localStorage.getItem('timesPerYear') || '1'
    );
    const [years, setYears] = useState(() => 
        localStorage.getItem('years') || ''
    );
    const [monthlyContribution, setMonthlyContribution] = useState(() => 
        localStorage.getItem('monthlyContribution') || ''
    );
    const [monthlyContributionIncrease, setMonthlyContributionIncrease] = useState(() => 
        localStorage.getItem('monthlyContributionIncrease') || '5'
    );
    const [error, setError] = useState(null);
    const [homePrice, setHomePrice] = useState(() => 
        localStorage.getItem('homePrice') || ''
    );
    const [homePriceIncrease, setHomePriceIncrease] = useState(() => 
        localStorage.getItem('homePriceIncrease') || ''
    );
    const [touchedFields, setTouchedFields] = useState({
        principal: false,
        annualInterestRate: false,
        years: false,
    });
    const [mortgageAmount, setMortgageAmount] = useState(() => 
        localStorage.getItem('mortgageAmount') || ''
    );
    const [mortgageIncreaseAnnually, setMortgageIncreaseAnnually] = useState(() => 
        localStorage.getItem('mortgageIncreaseAnnually') || '5'
    );
    const [analyticsConsent, setAnalyticsConsent] = useState(() => {
        const savedConsent = localStorage.getItem('analyticsConsent');
        return savedConsent ? JSON.parse(savedConsent) : null;
    });

    useEffect(() => {
        localStorage.setItem('investments', JSON.stringify(investments));
    }, [investments]);

    useEffect(() => {
        localStorage.setItem('timesPerYear', timesPerYear);
    }, [timesPerYear]);

    useEffect(() => {
        localStorage.setItem('years', years);
    }, [years]);

    useEffect(() => {
        localStorage.setItem('monthlyContribution', monthlyContribution);
    }, [monthlyContribution]);

    useEffect(() => {
        localStorage.setItem('monthlyContributionIncrease', monthlyContributionIncrease);
    }, [monthlyContributionIncrease]);

    useEffect(() => {
        localStorage.setItem('homePrice', homePrice);
    }, [homePrice]);

    useEffect(() => {
        localStorage.setItem('homePriceIncrease', homePriceIncrease);
    }, [homePriceIncrease]);

    useEffect(() => {
        localStorage.setItem('activeTab', activeTab);
    }, [activeTab]);

    useEffect(() => {
        localStorage.setItem('mortgageAmount', mortgageAmount);
    }, [mortgageAmount]);

    useEffect(() => {
        localStorage.setItem('mortgageIncreaseAnnually', mortgageIncreaseAnnually);
    }, [mortgageIncreaseAnnually]);

    useEffect(() => {
        // Initialize Google Analytics based on consent
        if (analyticsConsent === true) {
            // Enable Google Analytics
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        } else if (analyticsConsent === false) {
            // Disable Google Analytics
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied'
            });
        }
    }, [analyticsConsent]);

    const formatLargeNumber = (number) => {
        if (!number) return '';
        // First clean the input to ensure it's a valid number
        const cleanNumber = number.toString().replace(/[^\d.]/g, '');
        return cleanNumber ? Number(cleanNumber).toLocaleString('en-US').replace(/,/g, ' ') : '';
    };

    const handleNumberInput = (value, setter) => {
        // Remove any non-numeric characters (including spaces and commas)
        const cleanValue = value.replace(/[^\d.]/g, '');
        
        if (cleanValue) {
            const numberValue = parseFloat(cleanValue);
            if (!isNaN(numberValue)) {
                // Store the raw number value
                setter(numberValue.toString());
            }
        } else {
            setter('');
        }
    };

    const handleInvestmentChange = (id, field, value) => {
        setInvestments(prev => prev.map(investment => {
            if (investment.id === id) {
                if (field === 'principal') {
                    const cleanValue = value.replace(/[^\d.]/g, '');
                    return { 
                        ...investment, 
                        principal: cleanValue ? parseFloat(cleanValue).toString() : '' 
                    };
                }
                return { ...investment, [field]: value };
            }
            return investment;
        }));
    };

    const addInvestment = () => {
        setInvestments(prev => [
            ...prev,
            { 
                id: Math.max(...prev.map(i => i.id), 0) + 1,
                principal: '',
                annualInterestRate: ''
            }
        ]);
    };

    const removeInvestment = (id) => {
        if (investments.length > 1) {
            setInvestments(prev => prev.filter(investment => investment.id !== id));
        }
    };

    const validateField = (fieldName, value) => {
        if (!value || value.toString().trim() === '') {
            return 'This field is required';
        }
        return '';
    };

    const initiateCalculation = async () => {
        try {
            setError(null);

            // Calculate total principal and weighted average interest rate
            const totalPrincipal = investments.reduce((sum, inv) => 
                sum + parseFloat(inv.principal || 0), 0
            );

            const weightedInterestRate = investments.reduce((sum, inv) => 
                sum + (parseFloat(inv.principal || 0) * parseFloat(inv.annualInterestRate || 0)), 0
            ) / totalPrincipal;

            const response = await axios.post(`${API_BASE_URL}/api/calculation/calculate`, {
                principal: totalPrincipal,
                annualInterestRate: weightedInterestRate,
                timesPerYear: parseInt(timesPerYear, 10),
                years: parseInt(years, 10),
                monthlyContribution: monthlyContribution ? parseFloat(monthlyContribution) : 0,
                monthlyContributionIncreaseAnnually: monthlyContributionIncrease ? parseFloat(monthlyContributionIncrease) : 5,
                housePrice: homePrice ? parseFloat(homePrice) : 0,
                annualHousePriceIncrease: homePriceIncrease ? parseFloat(homePriceIncrease) : 0,
                mortgageAmount: mortgageAmount ? parseFloat(mortgageAmount) : 0,
                mortgageIncreaseAnnually: mortgageIncreaseAnnually ? parseFloat(mortgageIncreaseAnnually) : 0
            });
            
            setCalculationResult(response.data);
            
            setTimeout(() => {
                document.querySelector('.text-2xl.font-bold.text-gray-900.dark\\:text-white.mb-6').scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });                
            }, 100);

        } catch (error) {
            console.error("Error calculating results", error);
            setError("There was an error with the calculation. Please check your input values.");
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Mark all fields as touched
        setTouchedFields({
            principal: true,
            annualInterestRate: true,
            years: true,
        });

        // Validate required fields
        const hasEmptyInvestments = investments.some(inv => 
            !inv.principal || !inv.annualInterestRate
        );

        const hasEmptyRequiredFields = !years || hasEmptyInvestments;

        if (hasEmptyRequiredFields) {
            setError("Please fill in all required fields");
            return;
        }

        initiateCalculation();
    };

    const clearSavedData = () => {
        localStorage.removeItem('investments');
        localStorage.removeItem('timesPerYear');
        localStorage.removeItem('years');
        localStorage.removeItem('monthlyContribution');
        localStorage.removeItem('monthlyContributionIncrease');
        localStorage.removeItem('homePrice');
        localStorage.removeItem('homePriceIncrease');
        localStorage.removeItem('activeTab');
        localStorage.removeItem('mortgageAmount');
        localStorage.removeItem('mortgageIncreaseAnnually');
        
        // Reset state to initial values
        setInvestments([{ id: 1, principal: '', annualInterestRate: '' }]);
        setTimesPerYear('1');
        setYears('');
        setMonthlyContribution('');
        setMonthlyContributionIncrease('5');
        setHomePrice('');
        setHomePriceIncrease('');
        setActiveTab('investment');
        setMortgageAmount('');
        setMortgageIncreaseAnnually('5');
    };

    const calculateWeightedAverageRate = () => {
        const totalPrincipal = investments.reduce((sum, inv) => 
            sum + parseFloat(inv.principal || 0), 0
        );

        const weightedInterestRate = investments.reduce((sum, inv) => 
            sum + (parseFloat(inv.principal || 0) * parseFloat(inv.annualInterestRate || 0)), 0
        ) / totalPrincipal;

        return weightedInterestRate || 0;
    };

    const handleAnalyticsConsentChange = (consent) => {
        setAnalyticsConsent(consent);
    };

    return (
                <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
                    <Seo
                        title={getPageTitle(activeTab)}
                        description={getPageDescription(activeTab)}
                        path={location.pathname}
                    />
                    <AnalyticsConsent onConsentChange={handleAnalyticsConsentChange} />
                    <div className="max-w-4xl mx-auto px-4 py-8">
                        <Header clearSavedData={clearSavedData} />
                        {/* This for spacing */}
                        <div className="text-center mb-12"/>
                        <div className="text-center mb-12">
                            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                                Investment Calculator
                            </h1>
                        </div>

                        {/* Segmented Control */}
                        <div className="flex justify-center mb-8">
                            <div className="inline-flex rounded-lg bg-gray-200 dark:bg-gray-800 p-1 relative">
                                {/* Sliding background */}
                                <div
                                    className={`absolute inset-1 w-1/3 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 transition-transform duration-300 ease-out ${
                                        activeTab === 'house' ? 'translate-x-full' : 
                                        activeTab === 'mortgage' ? 'translate-x-[200%]' : 'translate-x-0'
                                    }`}
                                />
                                {/* Buttons */}
                                <button
                                    onClick={() => setActiveTab('investment')}
                                    className={`px-4 py-2 rounded-lg relative transition-colors duration-300 ease-out z-10 w-32 ${
                                        activeTab === 'investment' ? 'text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                    id="investment-tab"
                                    aria-label="Investment Calculator"
                                    aria-pressed={activeTab === 'investment'}
                                >
                                    Investment
                                </button>
                                <button
                                    onClick={() => setActiveTab('house')}
                                    className={`px-4 py-2 rounded-lg relative transition-colors duration-300 ease-out z-10 w-32 ${
                                        activeTab === 'house' ? 'text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                    id="house-tab"
                                    aria-label="House Investment Calculator"
                                    aria-pressed={activeTab === 'house'}
                                >
                                    House
                                </button>
                                <button
                                    onClick={() => setActiveTab('mortgage')}
                                    className={`px-4 py-2 rounded-lg relative transition-colors duration-300 ease-out z-10 w-32 ${
                                        activeTab === 'mortgage' ? 'text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                    id="mortgage-tab"
                                    aria-label="Mortgage Calculator"
                                    aria-pressed={activeTab === 'mortgage'}
                                >
                                    Mortgage
                                </button>
                            </div>
                        </div>

                        {/* Calculator Card */}
                        <div className="backdrop-blur-xl bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl p-8 shadow-2xl">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {/* Investment rows */}
                                    {investments.map((investment, index) => (
                                        <React.Fragment key={investment.id}>
                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text text-gray-700 dark:text-gray-300">
                                                        Principal Amount {index + 1} *
                                                    </span>
                                                </label>
                                                <label className="input-group">
                                                    <input
                                                        type="text"
                                                        value={formatLargeNumber(investment.principal)}
                                                        onChange={(e) => handleInvestmentChange(
                                                            investment.id,
                                                            'principal',
                                                            e.target.value
                                                        )}
                                                        className={`input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                                                            touchedFields.principal && !investment.principal ? 'border-red-400' : ''
                                                        }`}
                                                        placeholder="10 000"
                                                    />
                                                </label>
                                                {touchedFields.principal && !investment.principal && (
                                                    <label className="label">
                                                        <span className="label-text-alt text-red-400">Required</span>
                                                    </label>
                                                )}
                                            </div>

                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text text-gray-700 dark:text-gray-300">Interest Rate {index + 1} *</span>
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <div className="relative flex-1">
                                                        <input
                                                            type="number"
                                                            value={investment.annualInterestRate}
                                                            onChange={(e) => handleInvestmentChange(
                                                                investment.id,
                                                                'annualInterestRate',
                                                                e.target.value
                                                            )}
                                                            className={`input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-8 ${
                                                                touchedFields.annualInterestRate && !investment.annualInterestRate ? 'border-red-400' : ''
                                                            }`}
                                                            placeholder="5"
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
                                                    </div>
                                                    {/* Add/Remove buttons */}
                                                    <div className="flex gap-2">
                                                        {index === investments.length - 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={addInvestment}
                                                                className="p-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-all duration-200 hover:scale-110 hover:shadow-lg hover:shadow-emerald-500/25 active:scale-95 active:shadow-none"
                                                                title="Add new investment"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        {investments.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeInvestment(investment.id)}
                                                                className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 hover:scale-110 hover:shadow-lg hover:shadow-red-500/25 active:scale-95 active:shadow-none"
                                                                title="Remove investment"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {touchedFields.annualInterestRate && !investment.annualInterestRate && (
                                                    <label className="label">
                                                        <span className="label-text-alt text-red-400">Required</span>
                                                    </label>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    ))}

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700 dark:text-gray-300">Compound Frequency</span>
                                        </label>
                                        <select
                                            value={timesPerYear}
                                            onChange={(e) => setTimesPerYear(e.target.value)}
                                            className="select select-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        >
                                            <option value="1">Annually (1/year)</option>
                                            <option value="2">Semi-annually (2/year)</option>
                                            <option value="4">Quarterly (4/year)</option>
                                            <option value="12">Monthly (12/year)</option>
                                        </select>
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700 dark:text-gray-300">Investment Period *</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                value={years}
                                                onChange={(e) => {
                                                    setYears(e.target.value);
                                                    setTouchedFields(prev => ({ ...prev, years: true }));
                                                }}
                                                className={`input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                                    touchedFields.years && !years ? 'border-red-400' : ''
                                                }`}
                                                placeholder="10"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">years</span>
                                        </div>
                                        {touchedFields.years && !years && (
                                            <label className="label">
                                                <span className="label-text-alt text-red-400">Investment period is required</span>
                                            </label>
                                        )}
                                    </div>

                                    <div className="form-control md:col-span-2">
                                        <label className="label">
                                            <span className="label-text text-gray-700 dark:text-gray-300">Monthly Contribution</span>
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={formatLargeNumber(monthlyContribution)}
                                                    onChange={(e) => handleNumberInput(e.target.value, setMonthlyContribution)}
                                                    className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-16"
                                                    placeholder="Optional"
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">quarterly added</span>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={monthlyContributionIncrease}
                                                    onChange={(e) => setMonthlyContributionIncrease(e.target.value)}
                                                    className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    placeholder="5"
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">% yearly increase</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* House related inputs - visible when house or mortgage tab is selected */}
                                    {(activeTab === 'house' || activeTab === 'mortgage') && (
                                        <>
                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text text-gray-700 dark:text-gray-300">Target amount needed for Home Price</span>
                                                </label>
                                                <label className="input-group">
                                                    <input
                                                        type="text"
                                                        value={formatLargeNumber(homePrice)}
                                                        onChange={(e) => handleNumberInput(e.target.value, setHomePrice)}
                                                        className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                        placeholder="500 000"
                                                    />
                                                </label>
                                            </div>

                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text text-gray-700 dark:text-gray-300">Yearly Home Price Increase</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        value={homePriceIncrease}
                                                        onChange={(e) => setHomePriceIncrease(parseFloat(e.target.value) || '')}
                                                        className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        placeholder="3"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Mortgage input field when mortgage tab is active */}
                                    {activeTab === 'mortgage' && (
                                        <div className="form-control md:col-span-2">
                                            <label className="label">
                                                <span className="label-text text-gray-700 dark:text-gray-300">Mortgage Details</span>
                                            </label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={formatLargeNumber(mortgageAmount)}
                                                        onChange={(e) => handleNumberInput(e.target.value, setMortgageAmount)}
                                                        className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                        placeholder="300 000"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">amount</span>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        value={mortgageIncreaseAnnually}
                                                        onChange={(e) => setMortgageIncreaseAnnually(e.target.value)}
                                                        className="input input-bordered w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        placeholder="5"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">% yearly increase</span>
                                                </div>
                                            </div>
                                            <label className="label">
                                                <span className="label-text-alt text-gray-500 dark:text-gray-400 whitespace-normal">
                                                    Amount you are able to take, increases yearly by the specified percentage
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="text-center">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 rounded-lg relative transition-colors duration-300 ease-out z-10 w-50 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600"
                                    >
                                        Calculate Now
                                    </button>
                                </div>
                            </form>
                        </div>

                        {error && (
                            <div className="mt-8 alert alert-error shadow-lg">
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            </div>
                        )}

                        {calculationResult && (
                            <div id="results" className="mt-8 backdrop-blur-xl bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl p-8 shadow-2xl">
                                <CalculationResult 
                                    result={calculationResult}
                                    homePrice={activeTab === 'house' || activeTab === 'mortgage' ? Number(homePrice) : 0}
                                    homePriceIncrease={activeTab === 'house' || activeTab === 'mortgage' ? Number(homePriceIncrease) : 0}
                                    years={Number(years)}
                                    showHouseAnalysis={activeTab === 'house' || activeTab === 'mortgage'}
                                    averageInterestRate={calculateWeightedAverageRate()}
                                    mortgageAmount={Number(mortgageAmount)}
                                />
                            </div>
                        )}
                    </div>
                    <Footer />
                    <ChatButton />
                    <Chatbot />
                </div>
    );
}

export default App;
